import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import adminApi from '../api/client';

interface OverviewStats {
  users_by_tier: {
    sapphire?: number;
    emerald?: number;
    topaz?: number;
    platinum?: number;
    amethyst?: number;
    gold?: number;
    red_diamond?: number;
  };
  total_users: number;
  total_sellers: number;
  total_listings: number;
  total_orders: number;
  new_users_last_7_days: number;
  new_users_last_30_days: number;
}

interface UserGrowthData {
  date: string;
  count: number;
}

interface DateRangeMeta {
  start: string;
  end: string;
  label: string;
}

interface GrowthOverview {
  date_range: DateRangeMeta;
  total_users: number;
  new_users: number;
  new_users_by_gender?: Record<'male' | 'female' | 'not_specified', {
    count: number;
    percentage: number;
  }>;
  new_users_prev_period: number;
  growth_rate_pct: number;
  dau: number;
  wau: number;
  mau: number;
  total_catalog_books: number;
  total_library_entries: number;
  active_sellers: number;
  posts_in_range: number;
}

interface TrendsPoint {
  date: string;
  new_registrations: number;
  active_users: number;
}

interface TrendsResponse {
  date_range: DateRangeMeta;
  note: string;
  series: TrendsPoint[];
}

interface FunnelStep {
  label: string;
  count: number;
}

interface FunnelResponse {
  date_range: DateRangeMeta;
  note: string;
  steps: FunnelStep[];
}

type RangeOption = 'today' | '7d' | '30d' | '90d';

const COLORS: Record<string, string> = {
  sapphire: "#534AB7",
  emerald: "#1D9E75",
  topaz: "#BA7517",
  platinum: "#888780",
  amethyst: "#7C3AED",
  gold: "#D97706",
  red_diamond: "#D85A30"
};

const TIER_NAMES: Record<string, string> = {
  sapphire: "Sapphire",
  emerald: "Emerald",
  topaz: "Topaz",
  platinum: "Platinum",
  amethyst: "Amethyst",
  gold: "Gold",
  red_diamond: "Red Diamond"
};

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
    <div className="h-8 bg-gray-300 rounded w-1/3"></div>
  </div>
);

const Analytics: React.FC = () => {
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [growthData, setGrowthData] = useState<UserGrowthData[]>([]);
  const [loading, setLoading] = useState(true);

  const [range, setRange] = useState<RangeOption>('30d');
  const [growthOverview, setGrowthOverview] = useState<GrowthOverview | null>(null);
  const [trendsData, setTrendsData] = useState<TrendsResponse | null>(null);
  const [funnelData, setFunnelData] = useState<FunnelResponse | null>(null);
  const [growthLoading, setGrowthLoading] = useState(true);
  const [growthRetry, setGrowthRetry] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [overviewRes, growthRes] = await Promise.all([
          adminApi.get<OverviewStats>('/admin/analytics/overview'),
          adminApi.get<UserGrowthData[]>('/admin/analytics/user-growth')
        ]);
        setOverview(overviewRes.data);
        
        // Format dates for the chart (short format like "Mar 17")
        const formattedGrowth = growthRes.data.map(item => {
          const date = new Date(item.date);
          const shortDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          return { ...item, date: shortDate };
        });
        setGrowthData(formattedGrowth);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchGrowth = async () => {
      try {
        setGrowthLoading(true);
        setGrowthOverview(null);
        const [ovRes, trRes, fnRes] = await Promise.all([
          adminApi.get<GrowthOverview>(`/admin/analytics/growth/overview?range=${range}`),
          adminApi.get<TrendsResponse>(`/admin/analytics/growth/trends?range=${range}`),
          adminApi.get<FunnelResponse>(`/admin/analytics/growth/funnel?range=${range}`),
        ]);
        if (cancelled) return;
        setGrowthOverview(ovRes.data);
        setTrendsData(trRes.data);
        setFunnelData(fnRes.data);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch growth analytics:', error);
      } finally {
        if (!cancelled) setGrowthLoading(false);
      }
    };
    fetchGrowth();
    return () => { cancelled = true; };
  }, [range, growthRetry]);

  const pieData = overview ? Object.entries(overview.users_by_tier)
    .filter(([_, value]) => (value ?? 0) > 0)
    .map(([key, value]) => ({
      name: TIER_NAMES[key] || key,
      value: value ?? 0,
      colorKey: key
    })) : [];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Analytics Dashboard</h1>
        <button 
          onClick={() => window.location.reload()}
          className="p-2 bg-white rounded-xl border border-gray-200 text-gray-500 hover:text-primary transition-colors shadow-sm"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
             <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════
          GROWTH ANALYTICS SECTION (Phase A — new metrics)
          ═══════════════════════════════════════════════════ */}

      {/* A) Date Range Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-500 mr-2">Time range:</span>
        {(['today', '7d', '30d', '90d'] as RangeOption[]).map((opt) => (
          <button
            key={opt}
            onClick={() => setRange(opt)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              range === opt
                ? 'bg-[#085041] text-white shadow-sm'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-[#085041]'
            }`}
          >
            {opt === 'today' ? 'Today' : opt.toUpperCase()}
          </button>
        ))}
        {growthOverview && (
          <span className="ml-auto text-xs text-gray-400">
            {growthOverview.date_range.start} → {growthOverview.date_range.end}
          </span>
        )}
      </div>

      {/* B) Growth KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {growthLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : growthOverview && (
          <>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-[#1D9E75] shadow-sm">
              <p className="text-sm font-medium text-gray-500 mb-1">DAU (24h)</p>
              <p className="text-3xl font-bold text-gray-800">{growthOverview.dau.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Active in last 24 hours</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-[#BA7517] shadow-sm">
              <p className="text-sm font-medium text-gray-500 mb-1">WAU (7d)</p>
              <p className="text-3xl font-bold text-gray-800">{growthOverview.wau.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Active in last 7 days</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-[#534AB7] shadow-sm">
              <p className="text-sm font-medium text-gray-500 mb-1">MAU (30d)</p>
              <p className="text-3xl font-bold text-gray-800">{growthOverview.mau.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Active in last 30 days</p>
            </div>
            <div className={`bg-white rounded-2xl p-6 border-l-4 shadow-sm ${
              growthOverview.growth_rate_pct >= 0 ? 'border-[#1D9E75]' : 'border-[#D85A30]'
            }`}>
              <p className="text-sm font-medium text-gray-500 mb-1">Growth Rate</p>
              <p className={`text-3xl font-bold ${
                growthOverview.growth_rate_pct >= 0 ? 'text-[#1D9E75]' : 'text-[#D85A30]'
              }`}>
                {growthOverview.growth_rate_pct >= 0 ? '↑' : '↓'} {Math.abs(growthOverview.growth_rate_pct).toFixed(1)}%
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {growthOverview.new_users} new vs {growthOverview.new_users_prev_period} prev period
              </p>
            </div>
          </>
        )}
      </div>

      <section aria-label="New registration gender percentages" aria-busy={growthLoading}
        className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h2 className="text-lg font-bold text-gray-800">New Registrations by Gender</h2>
        <p className="text-xs text-gray-500 mt-1 mb-4">
          Share of male and female registrations in the selected period (UTC), using current profile gender.
          Not specified is excluded. Rounded to one decimal place.
        </p>
        {growthLoading ? (
          <p role="status" className="text-sm text-gray-500">Loading gender percentages...</p>
        ) : !growthOverview?.new_users_by_gender ? (
          <div className="text-sm text-gray-500">
            <p role="status">Gender breakdown unavailable.</p>
            <button onClick={() => setGrowthRetry(value => value + 1)}
              className="mt-2 text-primary font-semibold hover:underline">Retry</button>
          </div>
        ) : growthOverview.new_users === 0 ? (
          <p className="text-sm text-gray-500">No registrations in the selected period.</p>
        ) : growthOverview.new_users_by_gender.male.count + growthOverview.new_users_by_gender.female.count === 0 ? (
          <p className="text-sm text-gray-500">No male or female registrations in the selected period.</p>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {([
              ['male', 'Male'], ['female', 'Female'],
            ] as const).map(([key, label]) => (
              <div key={key}>
                <dt className="text-sm font-medium text-gray-500">{label}</dt>
                <dd className="text-2xl font-bold text-gray-800">
                  {growthOverview.new_users_by_gender![key].percentage.toFixed(1)}%
                </dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      {/* C) Trends Line Chart */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-800">Growth Trends</h2>
          {trendsData?.note && (
            <span className="text-xs text-gray-400 italic max-w-md text-right">
              ℹ️ {trendsData.note}
            </span>
          )}
        </div>
        {growthLoading ? (
          <div className="h-[300px] flex items-center justify-center bg-gray-50 rounded-xl animate-pulse" />
        ) : trendsData && (
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={trendsData.series.map((p) => ({
                  ...p,
                  date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                }))}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} dx={-10} />
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                <Line
                  type="monotone"
                  dataKey="new_registrations"
                  name="New Registrations"
                  stroke="#085041"
                  strokeWidth={3}
                  dot={{ fill: '#085041', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, strokeWidth: 0 }}
                />
                <Line
                  type="monotone"
                  dataKey="active_users"
                  name="Active Users"
                  stroke="#BA7517"
                  strokeWidth={3}
                  dot={{ fill: '#BA7517', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* D) Registration Funnel */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-800">Registration Funnel</h2>
          {funnelData?.note && (
            <span className="text-xs text-gray-400 italic max-w-md text-right">
              ℹ️ {funnelData.note}
            </span>
          )}
        </div>
        {growthLoading ? (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : funnelData && funnelData.steps.length > 0 && (
          <div className="space-y-3">
            {funnelData.steps.map((step, idx) => {
              const topCount = funnelData.steps[0].count || 1;
              const widthPct = Math.max((step.count / topCount) * 100, 4);
              const prevCount = idx > 0 ? funnelData.steps[idx - 1].count : null;
              const dropPct = prevCount && prevCount > 0
                ? Math.round(((prevCount - step.count) / prevCount) * 100)
                : null;
              const colors = ['#085041', '#1D9E75', '#BA7517', '#D85A30'];
              return (
                <div key={step.label} className="flex items-center gap-4">
                  <div className="w-40 text-sm font-medium text-gray-700 text-right">
                    {step.label}
                  </div>
                  <div className="flex-1 relative h-12 bg-gray-50 rounded-xl overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 flex items-center px-4 text-white font-semibold rounded-xl transition-all"
                      style={{ width: `${widthPct}%`, backgroundColor: colors[idx % colors.length] }}
                    >
                      {step.count.toLocaleString()}
                    </div>
                  </div>
                  <div className="w-24 text-xs text-gray-400 text-left">
                    {dropPct !== null && dropPct > 0 && (
                      <span className="text-[#D85A30]">↓ {dropPct}% drop</span>
                    )}
                    {idx === 0 && <span>Base</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════
          END GROWTH ANALYTICS SECTION
          ═══════════════════════════════════════════════════ */}

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : overview && (
          <>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-[#085041] shadow-sm">
              <p className="text-sm font-medium text-gray-500 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-800">{(overview.total_users ?? 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-[#1D9E75] shadow-sm">
              <p className="text-sm font-medium text-gray-500 mb-1">New (7 days)</p>
              <p className="text-3xl font-bold text-gray-800">{(overview.new_users_last_7_days ?? 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-[#BA7517] shadow-sm">
              <p className="text-sm font-medium text-gray-500 mb-1">New (30 days)</p>
              <p className="text-3xl font-bold text-gray-800">{(overview.new_users_last_30_days ?? 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-[#D85A30] shadow-sm">
              <p className="text-sm font-medium text-gray-500 mb-1">Total Sellers</p>
              <p className="text-3xl font-bold text-gray-800">{(overview.total_sellers ?? 0).toLocaleString()}</p>
            </div>
          </>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Line Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold text-gray-800 mb-6">User Registrations (Last 30 Days)</h2>
          {loading ? (
            <div className="h-[300px] flex items-center justify-center bg-gray-50 rounded-xl animate-pulse" />
          ) : (
             <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={growthData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 12 }} 
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 12 }} 
                    dx={-10}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#1D9E75" 
                    strokeWidth={3} 
                    dot={{ fill: '#1D9E75', strokeWidth: 2, r: 4 }} 
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Pie Chart */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold text-gray-800 mb-6">Reader Tier Distribution</h2>
          {loading ? (
             <div className="h-[300px] flex items-center justify-center bg-gray-50 rounded-xl animate-pulse" />
          ) : pieData.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center flex-col text-gray-400">
              <span className="text-4xl mb-2">📊</span>
              <p>No tier data available</p>
            </div>
          ) : (
            <div className="h-[300px] w-full flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[entry.colorKey] || '#ccc'} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    itemStyle={{ color: '#374151', fontWeight: 600 }}
                  />
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="bottom" 
                    align="center"
                    iconType="circle"
                    wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {loading ? (
           <>
             <SkeletonCard />
             <SkeletonCard />
           </>
        ) : overview && (
          <>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm flex items-center justify-between">
               <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Total Listings</p>
                  <p className="text-2xl font-bold text-gray-800">{(overview.total_listings ?? 0).toLocaleString()}</p>
               </div>
               <div className="w-12 h-12 rounded-full bg-[#085041]/10 flex items-center justify-center">
                 <span className="text-xl">📚</span>
               </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm flex items-center justify-between">
               <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-800">{(overview.total_orders ?? 0).toLocaleString()}</p>
               </div>
               <div className="w-12 h-12 rounded-full bg-[#1D9E75]/10 flex items-center justify-center">
                 <span className="text-xl">📦</span>
               </div>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default Analytics;
